/**
 * Private vault helpers for computer-use across desktop, hosted computer-use,
 * and mobile. Secrets never enter chat text or Jev state. User-facing name is
 * Private vault (not Muse/Vesper).
 */

import type { UserCredentialSurface } from '@/lib/user-credentials/user-credential-vault'

export type ComputerUseVaultClient = 'desktop' | 'computer_use' | 'mobile'

/**
 * Surfaces granted when saving a login from any computer-use client so the
 * same credential can be reused on desktop, hosted computer-use, and mobile.
 */
export const COMPUTER_USE_VAULT_ALLOWED_SURFACES: UserCredentialSurface[] = [
  'tools',
  'sandbox',
  'chat',
  'computer_use',
  'desktop',
  'mobile'
]

export function computerUseVaultSurfacesForClient(
  client: ComputerUseVaultClient
): UserCredentialSurface[] {
  // Always grant the shared computer-use set. The client tag is metadata for
  // events / audit, not a narrower allowlist, so a mobile-saved login still
  // works on desktop computer-use and vice versa.
  void client
  return [...COMPUTER_USE_VAULT_ALLOWED_SURFACES]
}

export function credentialAllowsComputerUse(
  allowedSurfaces: readonly string[] | null | undefined
): boolean {
  if (!Array.isArray(allowedSurfaces) || allowedSurfaces.length === 0) {
    return false
  }

  return allowedSurfaces.some(surface =>
    (COMPUTER_USE_VAULT_ALLOWED_SURFACES as readonly string[]).includes(surface)
  )
}

export function computerUseVaultCredentialKey(domain: string): string {
  const normalized = domain.trim().toLowerCase().replace(/[^a-z0-9]+/gi, '_')
  return `${normalized || 'site'}_login`
}

export function computerUseVaultCredentialName(domain: string): string {
  const normalized = domain.trim().toLowerCase() || 'site'
  return `${normalized} login`
}

export function buildComputerUseLoginSecretValue(input: {
  password: string
  username: string
}): string {
  return JSON.stringify({
    password: input.password.trim(),
    username: input.username.trim()
  })
}

export {
  buildVaultSecretValueFromFields,
  DEFAULT_LOGIN_VAULT_FIELDS,
  inferVaultCredentialKind,
  isDefaultLoginFields,
  normalizeVaultRequestFields,
  readVaultUploadAsText,
  type VaultRequestField,
  type VaultRequestFieldType
} from '@/lib/computer-use/vault-request-fields'

export function buildComputerUseVaultMetadata(input: {
  client: ComputerUseVaultClient
  domain: string
  url?: string | null
  sessionId?: string | null
}): Record<string, unknown> {
  return {
    computer_use: true,
    client: input.client,
    domain: input.domain.trim().toLowerCase(),
    url: input.url?.trim() || null,
    session_id: input.sessionId || null,
    stored_via: 'private_vault'
  }
}
